import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { CLICK_AND_COLLECT_ID, EXPRESS_SHIPPING_METHOD_IDS } from '~/config/shipping'
import type { PageContentType } from '~/helpers/datalayer/types'
import { getProductPrice, mapPrices } from '~/helpers/product-pricing'
import type { BasketWithProductData, ProductItemWithProductData } from '~/types/Basket'
import type { FlattenedProduct } from '~/types/Product'
import type { FlattenedWishlist, FlattenedWishlistItem } from '~/types/Wishlist'
import { asString, isRecord } from '~/utils/decode'

/**
 * Rules engine for Braze campaigns. CRM sets a `rules` key on the campaign — an in-app message's
 * extras or a banner's custom properties — and it is only shown when every rule passes, so a
 * campaign that depends on live storefront state (cart contents, current page, the product being
 * viewed) is discarded before the customer ever sees it.
 *
 * Marketing-facing catalogue and syntax: docs/braze-campaign-rules.md
 */

/** A coupon on the basket that SFCC accepted but that discounts nothing. */
const NO_APPLICABLE_PROMOTION = 'no_applicable_promotion'

const SAFE_ARG = /^[A-Za-z0-9_-]{1,64}$/
/** URL fragments carry more than a slug: MHJ product paths end `.html`, and queries use `?`, `=` and `&`. */
const SAFE_PATH_ARG = /^[A-Za-z0-9_\-/.?=&%]{1,120}$/

/** Separates the values of a rule that passes when any one of them matches. */
export const VALUE_SEPARATOR = '|'
/** Keeps a value list reviewable; a longer one is far more likely a paste error than a campaign. */
export const MAX_VALUES = 20

/** KVP page names, kept hyphenated so CRM never has to type a space. */
const PAGE_NAMES = new Map<string, PageContentType>([
  ['home', 'home'],
  ['plp', 'plp'],
  ['pdp', 'pdp'],
  ['article', 'article'],
  ['blog-landing', 'blog landing'],
  ['content-page', 'content-page'],
  ['cart', 'cart'],
  ['wishlist', 'wishlist'],
  ['my-account', 'my account'],
  ['checkout', 'checkout'],
  ['store-locator', 'store locator'],
  ['store-detail', 'store detail'],
  ['contact-us', 'contact us'],
  ['account-activation', 'account activation'],
  ['search-results', 'search results'],
])

/** CA and CA-FR are separate CRM markets despite sharing an SFCC site. */
export const SITE_CODES = ['AU', 'NZ', 'CA', 'CA-FR'] as const
export type CampaignSiteCode = (typeof SITE_CODES)[number]

const PRICING_TYPES = ['single_book', 'double_book'] as const

export type CampaignRuleContext = {
  pageType: PageContentType
  /** Route path *with* its query string, locale prefix included — `url_contains` advertises both. */
  fullPath: string
  site: CampaignSiteCode | undefined
  basket: BasketWithProductData | undefined
  pdpProduct: FlattenedProduct | undefined
  wishlist: FlattenedWishlist
  isLoggedIn: boolean
  preferredStoreId: string | undefined
}

type CampaignRule = {
  /** Reads the cart, which loads after the SDK can deliver a campaign. */
  needsCart?: boolean
  /** Reads the wishlist, which loads after the SDK can deliver a campaign. */
  needsWishlist?: boolean
  /** Also reads each wishlisted product, whose request can fail while the list itself loaded. */
  needsWishlistProducts?: boolean
  /** Reads the PDP product, which is stale mid-navigation until the swap completes. */
  needsProduct?: boolean
  arg?: 'name' | 'path' | 'number'
  argOptions?: readonly string[]
  /** Takes exactly one value, where a `|` list would be ambiguous. Number rules always do. */
  singleValue?: boolean
  test: (ctx: CampaignRuleContext, arg: string) => boolean
  /** Picker copy for the rule builder on /debug/braze. */
  describe: string
  /** Field label for the rule's value, shown next to the picker. */
  argLabel?: string
}

const lineItems = (ctx: CampaignRuleContext) => ctx.basket?.productItems ?? []

const cartUnitCount = (ctx: CampaignRuleContext) =>
  lineItems(ctx).reduce((units, item) => units + (item.quantity ?? 1), 0)

/** CRM rules use display-name attributes because basket variants omit `primaryCategoryId`. */
const CATEGORY_ATTRIBUTES = {
  retail_category: { key: 'c_mHJRetailCategory', label: 'Retail category' },
  retail_sub_category: { key: 'c_mHJRetailSubCategory', label: 'Retail subcategory' },
  primary_category: { key: 'c_mHJPrimaryCategory', label: 'Primary category' },
  sub_category: { key: 'c_mHJSubCategory', label: 'Subcategory' },
} as const

/** Lets `rings`, `Rings` and `engagement-rings` all match 'Engagement Rings'. */
const normalizeCategory = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const attributeMatches = (value: string | undefined, arg: string) =>
  value !== undefined && normalizeCategory(value) === normalizeCategory(arg)

const itemAttribute = (item: ProductItemWithProductData, key: string): string | undefined => {
  const attributes = item.productCustomAttributes
  return isRecord(attributes) ? asString(attributes[key]) : undefined
}

/** Preserve zero-priced gifts while leaving lines without a price incomparable. */
const itemUnitPrice = (item: ProductItemWithProductData): number | undefined => {
  const quantity = item.quantity ?? 1
  const lineTotal = item.priceAfterItemDiscount ?? item.price
  if (lineTotal !== undefined && quantity > 0) return lineTotal / quantity
  return item.basePrice
}

const cartTotal = (ctx: CampaignRuleContext): number | undefined => ctx.basket?.orderTotal

const pdpDisplayPrice = (product: FlattenedProduct, isLoggedIn: boolean): number | undefined => {
  if (!product.tieredPrices?.length && product.price === undefined) return undefined
  const price = getProductPrice(product.tieredPrices, isLoggedIn, product.price)
  // getProductPrice returns 0 both for "free" and for a tier table it could not map.
  return price > 0 ? price : undefined
}

const compare = (value: number | undefined, test: (value: number) => boolean) => value !== undefined && test(value)

/** Available to sell across the network — not store-level stock. Undefined when no inventory record came back. */
const availableToSell = (inventory: ShopperProductsTypes.Inventory | undefined) => inventory?.ats

const inStock = (inventory: ShopperProductsTypes.Inventory | undefined) =>
  inventory?.orderable === true && (availableToSell(inventory) ?? 0) > 0

/** Match `Pricing.vue` precedence: single wins, while double requires a retail strike-through. */
const pricingType = (tieredPrices: ShopperProductsTypes.ProductPriceTable[] | undefined): string | undefined => {
  const prices = tieredPrices && mapPrices(tieredPrices)
  if (!prices) return undefined
  if (prices.singlePrice) return 'single_book'
  if (prices.doublePrice && prices.retailPrice) return 'double_book'
  return undefined
}

const shippingMethodId = (ctx: CampaignRuleContext) => ctx.basket?.shipments?.[0]?.shippingMethod?.id

const SHIPPING_METHODS = new Map<string, (id: string) => boolean>([
  ['delivery', (id) => id !== CLICK_AND_COLLECT_ID],
  ['click-and-collect', (id) => id === CLICK_AND_COLLECT_ID],
  ['express', (id) => EXPRESS_SHIPPING_METHOD_IDS.includes(id)],
])

const isShippingMethod = (ctx: CampaignRuleContext, arg: string) => {
  const id = shippingMethodId(ctx)
  // Nothing chosen yet is no method at all, not Delivery by default.
  return id !== undefined && SHIPPING_METHODS.get(arg.toLowerCase())?.(id) === true
}

const appliedCoupons = (ctx: CampaignRuleContext) =>
  ctx.basket?.couponItems?.filter((item) => item.valid === true && item.statusCode !== NO_APPLICABLE_PROMOTION) ?? []

const onPdp = (ctx: CampaignRuleContext, test: (product: FlattenedProduct) => boolean) =>
  ctx.pdpProduct ? test(ctx.pdpProduct) : false

const someItem = (ctx: CampaignRuleContext, test: (item: ProductItemWithProductData) => boolean) =>
  lineItems(ctx).some(test)

/** A wishlist item whose product could not be read never matches, so a `not_` rule passes for it. */
const someWishlistProduct = (ctx: CampaignRuleContext, test: (product: FlattenedProduct) => boolean) =>
  ctx.wishlist.some((item: FlattenedWishlistItem) => (item.flatProduct ? test(item.flatProduct) : false))

/** Qualifying promotions: what the product advertises, not what SFCC has discounted a line by. */
const qualifiesFor = (promotions: ShopperProductsTypes.ProductPromotion[] | undefined, arg: string) =>
  promotions?.some(({ promotionId }) => promotionId?.toLowerCase() === arg.toLowerCase()) === true

/** D365 pads store numbers to four digits; SFCC and the storefront don't. */
const storeNumber = (value: string) => value.toLowerCase().replace(/^0+(?=.)/, '')

const categoryRules = (scope: 'cart' | 'pdp'): [string, CampaignRule][] =>
  Object.entries(CATEGORY_ATTRIBUTES).map(([suffix, { key, label }]) => [
    `${scope}_has_${suffix}`,
    {
      ...(scope === 'cart' ? { needsCart: true } : { needsProduct: true }),
      arg: 'name',
      argLabel: label,
      describe:
        scope === 'cart'
          ? `An item in the cart has this ${label.toLowerCase()}`
          : `The product being viewed has this ${label.toLowerCase()}`,
      test: (ctx, arg) =>
        scope === 'cart'
          ? someItem(ctx, (item) => attributeMatches(itemAttribute(item, key), arg))
          : onPdp(ctx, (product) => attributeMatches(asString(product[key]), arg)),
    },
  ])

export const CAMPAIGN_RULES = new Map<string, CampaignRule>([
  ...Object.entries({
    page_type_is: {
      arg: 'name',
      argLabel: 'Page',
      argOptions: [...PAGE_NAMES.keys()],
      describe: 'Customer is on this page type',
      test: (ctx, arg) => ctx.pageType === PAGE_NAMES.get(arg.toLowerCase()),
    },
    on_checkout: { describe: 'Customer is anywhere in checkout', test: (ctx) => ctx.pageType === 'checkout' },
    url_contains: {
      arg: 'path',
      argLabel: 'Text in the URL',
      describe: 'Current URL contains this text',
      test: (ctx, arg) => ctx.fullPath.toLowerCase().includes(arg.toLowerCase()),
    },
    site_is: {
      arg: 'name',
      argLabel: 'Site',
      argOptions: SITE_CODES,
      describe: 'Customer is on this site',
      test: (ctx, arg) => ctx.site === arg.toUpperCase(),
    },

    items_in_cart: {
      needsCart: true,
      describe: 'Cart has at least one item',
      test: (ctx) => lineItems(ctx).length > 0,
    },
    cart_item_count_gte: {
      needsCart: true,
      arg: 'number',
      argLabel: 'Number of units',
      describe: 'Cart holds this many units or more — quantity counts, not lines',
      test: (ctx, arg) => cartUnitCount(ctx) >= Number(arg),
    },
    cart_total_gte: {
      needsCart: true,
      arg: 'number',
      argLabel: 'Cart total',
      describe: 'Cart total is this amount or more',
      test: (ctx, arg) => compare(cartTotal(ctx), (total) => total >= Number(arg)),
    },
    cart_has_coupon: {
      needsCart: true,
      describe: 'A working discount code is already applied',
      test: (ctx) => appliedCoupons(ctx).length > 0,
    },
    cart_missing_coupon: {
      needsCart: true,
      arg: 'name',
      argLabel: 'Coupon code',
      singleValue: true,
      describe: 'This code is not applied yet',
      test: (ctx, arg) => !appliedCoupons(ctx).some((item) => item.code?.toLowerCase() === arg.toLowerCase()),
    },
    cart_has_sku: {
      needsCart: true,
      arg: 'name',
      argLabel: 'SKU',
      describe: 'This SKU is in the cart',
      test: (ctx, arg) =>
        someItem(ctx, (item) => [item.productId, item.masterId].some((id) => id?.toLowerCase() === arg.toLowerCase())),
    },
    cart_has_collection: {
      needsCart: true,
      arg: 'name',
      argLabel: 'Collection name',
      describe: 'An item in the cart is in this collection',
      test: (ctx, arg) => someItem(ctx, (item) => attributeMatches(itemAttribute(item, 'c_mHJCollection'), arg)),
    },
    cart_item_inventory_lt: {
      needsCart: true,
      arg: 'number',
      argLabel: 'Units available',
      describe: 'An item in the cart has fewer than this many units available',
      test: (ctx, arg) => someItem(ctx, (item) => compare(availableToSell(item.inventory), (ats) => ats < Number(arg))),
    },
    cart_has_pricing_type_item: {
      needsCart: true,
      arg: 'name',
      argLabel: 'Pricing type',
      argOptions: PRICING_TYPES,
      describe: 'An item in the cart has this pricing type',
      test: (ctx, arg) => someItem(ctx, (item) => pricingType(item.productPrices) === arg.toLowerCase()),
    },
    cart_item_price_gte: {
      needsCart: true,
      arg: 'number',
      argLabel: 'Item price',
      describe: 'An item in the cart costs this much or more',
      test: (ctx, arg) => someItem(ctx, (item) => compare(itemUnitPrice(item), (price) => price >= Number(arg))),
    },
    cart_shipping_method: {
      needsCart: true,
      arg: 'name',
      argLabel: 'Fulfilment method',
      argOptions: [...SHIPPING_METHODS.keys()],
      describe: 'Cart is set to this fulfilment method',
      test: isShippingMethod,
    },
    cart_has_promo_id: {
      needsCart: true,
      arg: 'name',
      argLabel: 'Promotion ID',
      describe: 'An item in the cart qualifies for this promotion',
      test: (ctx, arg) => someItem(ctx, (item) => qualifiesFor(item.productPromotions, arg)),
    },

    has_wishlist_items: {
      needsWishlist: true,
      describe: 'Wishlist has at least one item',
      test: (ctx) => ctx.wishlist.length > 0,
    },
    wishlist_has_promo_id: {
      needsWishlist: true,
      needsWishlistProducts: true,
      arg: 'name',
      argLabel: 'Promotion ID',
      describe: 'An item in the wishlist qualifies for this promotion',
      test: (ctx, arg) => someWishlistProduct(ctx, (product) => qualifiesFor(product.productPromotions, arg)),
    },
    wishlist_has_pricing_type_item: {
      needsWishlist: true,
      needsWishlistProducts: true,
      arg: 'name',
      argLabel: 'Pricing type',
      argOptions: PRICING_TYPES,
      describe: 'An item in the wishlist has this pricing type',
      test: (ctx, arg) =>
        someWishlistProduct(ctx, (product) => pricingType(product.tieredPrices) === arg.toLowerCase()),
    },
    wishlist_item_inventory_lt: {
      needsWishlist: true,
      needsWishlistProducts: true,
      arg: 'number',
      argLabel: 'Units available',
      describe: 'An item in the wishlist has fewer than this many units available',
      test: (ctx, arg) =>
        someWishlistProduct(ctx, (product) => compare(availableToSell(product.inventory), (ats) => ats < Number(arg))),
    },

    pdp_has_stock: {
      needsProduct: true,
      describe: 'The product being viewed is in stock',
      test: (ctx) => onPdp(ctx, (product) => inStock(product.inventory)),
    },
    pdp_inventory_lt: {
      needsProduct: true,
      arg: 'number',
      argLabel: 'Units available',
      describe: 'The product being viewed has fewer than this many units available',
      test: (ctx, arg) =>
        onPdp(ctx, (product) => compare(availableToSell(product.inventory), (ats) => ats < Number(arg))),
    },
    pdp_sku_is: {
      needsProduct: true,
      arg: 'name',
      argLabel: 'SKU',
      describe: 'The product being viewed is this SKU',
      test: (ctx, arg) =>
        onPdp(ctx, (product) =>
          [product.id, product.master?.masterId].some((id) => id?.toLowerCase() === arg.toLowerCase()),
        ),
    },
    pdp_collection_is: {
      needsProduct: true,
      arg: 'name',
      argLabel: 'Collection name',
      describe: 'The product being viewed is in this collection',
      test: (ctx, arg) => onPdp(ctx, (product) => attributeMatches(product.c_mHJCollection, arg)),
    },
    pdp_pricing_type_is: {
      needsProduct: true,
      arg: 'name',
      argLabel: 'Pricing type',
      argOptions: PRICING_TYPES,
      describe: 'The product being viewed has this pricing type',
      test: (ctx, arg) => onPdp(ctx, (product) => pricingType(product.tieredPrices) === arg.toLowerCase()),
    },
    pdp_price_gte: {
      needsProduct: true,
      arg: 'number',
      argLabel: 'Price',
      describe: 'The product being viewed costs this much or more',
      test: (ctx, arg) =>
        onPdp(ctx, (product) => compare(pdpDisplayPrice(product, ctx.isLoggedIn), (price) => price >= Number(arg))),
    },

    logged_in: { describe: 'Customer is signed in', test: (ctx) => ctx.isLoggedIn },
    has_preferred_store: { describe: 'Customer has chosen a My Store', test: (ctx) => Boolean(ctx.preferredStoreId) },
    my_store_is: {
      arg: 'name',
      argLabel: 'Store number',
      describe: "Customer's My Store is this store number (478, not D365's 0478)",
      test: (ctx, arg) => ctx.preferredStoreId !== undefined && storeNumber(ctx.preferredStoreId) === storeNumber(arg),
    },
  } satisfies Record<string, CampaignRule>),
  ...categoryRules('cart'),
  ...categoryRules('pdp'),
])

export type ParsedCampaignRule = {
  name: string
  /** The rule passes when any one of these does. A rule that takes no value has a single empty one. */
  values: string[]
  negated: boolean
}

export type ParsedCampaignRules =
  | {
      rules: ParsedCampaignRule[]
      needsCart: boolean
      needsWishlist: boolean
      needsWishlistProducts: boolean
      needsProduct: boolean
    }
  /** Any malformed token invalidates the whole string — the campaign is suppressed, not partly applied. */
  | { error: string }

/** Whether a rule accepts a `|` list of values. */
export const takesManyValues = (rule: Pick<CampaignRule, 'arg' | 'singleValue'>) =>
  (rule.arg === 'name' || rule.arg === 'path') && !rule.singleValue

const valueError = (name: string, rule: CampaignRule, value: string): string | undefined => {
  if (rule.arg === 'name' && !SAFE_ARG.test(value)) return `invalid value "${value}" for rule "${name}"`
  if (rule.arg === 'path' && !SAFE_PATH_ARG.test(value)) return `invalid value "${value}" for rule "${name}"`
  if (rule.arg === 'number' && !/^\d+(\.\d+)?$/.test(value)) return `rule "${name}" needs a number, got "${value}"`
  if (rule.argOptions && !rule.argOptions.some((option) => option.toLowerCase() === value.toLowerCase())) {
    return `rule "${name}" only accepts ${rule.argOptions.join(', ')} — got "${value}"`
  }
  return undefined
}

/** A missing or blank value means the campaign opted out of rules, which parses to an always-passing list. */
export const parseCampaignRules = (raw: string | undefined): ParsedCampaignRules => {
  if (!raw)
    return { rules: [], needsCart: false, needsWishlist: false, needsWishlistProducts: false, needsProduct: false }

  const tokens = raw
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
  if (!tokens.length) return { error: `no rules found in "${raw}"` }

  const rules: ParsedCampaignRule[] = []
  for (const token of tokens) {
    const [rawHead = '', ...rest] = token.split(':')
    // Rule names are normalised; values are not — SKUs and coupon codes keep their case.
    const head = rawHead.trim().toLowerCase()
    const negated = head.startsWith('not_')
    const name = negated ? head.slice('not_'.length) : head
    if (head === 'rules') {
      return { error: 'the value starts with "rules:" — put "rules" in the key field and only the rest in the value' }
    }
    const rule = CAMPAIGN_RULES.get(name)
    if (!rule) return { error: `unknown rule "${token}"` }

    const arg = rest.join(':').trim()
    if (!rule.arg && arg) return { error: `rule "${name}" takes no value` }
    if (rule.arg && !arg) return { error: `rule "${name}" requires a value, e.g. ${name}:<value>` }

    const values = arg.split(VALUE_SEPARATOR).map((value) => value.trim())
    if (values.length > 1 && !takesManyValues(rule)) {
      return { error: `rule "${name}" takes one value, not a "${VALUE_SEPARATOR}" list` }
    }
    if (values.length > MAX_VALUES) return { error: `rule "${name}" has more than ${MAX_VALUES} values` }
    if (rule.arg && values.some((value) => !value)) return { error: `rule "${name}" has an empty value in "${arg}"` }

    for (const value of rule.arg ? values : []) {
      const error = valueError(name, rule, value)
      if (error) return { error }
    }

    rules.push({ name, values, negated })
  }

  const needs = (flag: 'needsCart' | 'needsWishlist' | 'needsWishlistProducts' | 'needsProduct') =>
    rules.some(({ name }) => CAMPAIGN_RULES.get(name)?.[flag])
  return {
    rules,
    needsCart: needs('needsCart'),
    needsWishlist: needs('needsWishlist'),
    needsWishlistProducts: needs('needsWishlistProducts'),
    needsProduct: needs('needsProduct'),
  }
}

/** Every rule must pass; a rule passes when any of its values does, and `not_` then inverts it. */
export const evaluateCampaignRules = (rules: ParsedCampaignRule[], ctx: CampaignRuleContext): boolean =>
  rules.every(({ name, values, negated }) => {
    // parseCampaignRules is the only source of ParsedCampaignRule, and it rejects names that aren't registered.
    const rule = CAMPAIGN_RULES.get(name)
    const passed = values.some((value) => rule?.test(ctx, value) === true)
    return negated ? !passed : passed
  })

export type CampaignRuleOption = {
  name: string
  describe: string
  argLabel?: string
  argType?: 'name' | 'path' | 'number'
  /** Fixed set of allowed values, so the builder can offer a dropdown instead of free text. */
  argOptions?: readonly string[]
  /** Accepts a `|` list, passing when any value matches. */
  manyValues: boolean
}

/** Everything the rule builder on /debug/braze needs to offer every rule. */
export const CAMPAIGN_RULE_OPTIONS: CampaignRuleOption[] = [...CAMPAIGN_RULES.entries()].map(([name, rule]) => ({
  name,
  describe: rule.describe,
  argLabel: rule.argLabel,
  argType: rule.arg,
  argOptions: rule.argOptions,
  manyValues: takesManyValues(rule),
}))

export type CampaignRuleRow = {
  name: string
  negated: boolean
  /** A number input binds a number, not a string. */
  value: string | number
}

/** Builds the `rules` KVP value from the builder's rows. Blank values are left in so the parser reports them. */
export const formatCampaignRules = (rows: CampaignRuleRow[]): string =>
  rows
    .filter(({ name }) => name)
    .map(({ name, negated, value }) => {
      const arg = String(value ?? '').trim()
      return `${negated ? 'not_' : ''}${name}${arg ? `:${arg}` : ''}`
    })
    .join(',')
